import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import productItems from "../../data/ProductItems";
import ModelViewer from "../ModelViewer/ModelViewer";
import AIRecommendationService from "../../services/AIRecommendationService";
import AuthenticityBadge from "../AuthenticityBadge/AuthenticityBadge";
import "./ProductList.css";
import LazyLoad from "react-lazyload";

const ProductList = ({ addToWishlist, wishlist, removeFromWishlist }) => {
  const [recommendations, setRecommendations] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(productItems);
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [aiFilteredProducts, setAiFilteredProducts] = useState(null);

  // ✅ ENHANCED - Dynamic AI Recommendations
  const generateAIRecommendations = () => {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const wishlistIds = wishlist.map(item => item.id);
    const hourOfDay = new Date().getHours();
    const rotationSeed = Math.floor(hourOfDay / 2);
    
    const scoredProducts = productItems.map((product, index) => {
      let score = 50;
      if (wishlistIds.includes(product.id)) score += 15;
      const viewIndex = recentlyViewed.indexOf(product.id);
      if (viewIndex !== -1) score += (5 - viewIndex) * 5;
      score += (index + rotationSeed) % productItems.length / productItems.length * 10;
      score += (parseFloat(product.rating) || 0) * 3;
      return { ...product, aiScore: Math.round(score) };
    });
    return scoredProducts.sort((a, b) => b.aiScore - a.aiScore).slice(0, 4);
  };

  useEffect(() => {
    productItems.forEach(product => {
      AIRecommendationService.trackProductView(product.id, product.category || 'General', 3000);
    });
  }, []);

  useEffect(() => {
    const enhanced = generateAIRecommendations();
    setRecommendations(enhanced);
  }, [wishlist, location.key]);

  useEffect(() => {
    const aiSearchResults = sessionStorage.getItem('aiSearchResults');
    if (aiSearchResults) {
      const searchResults = JSON.parse(aiSearchResults);
      setAiFilteredProducts(searchResults);
      setFilteredProducts(searchResults);
      sessionStorage.removeItem('aiSearchResults');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.key]);

  useEffect(() => {
    let filtered = aiFilteredProducts || productItems;
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => (p.category || 'General') === selectedCategory);
    }
    if (searchTerm) {
      filtered = filtered.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    setFilteredProducts(filtered);
  }, [selectedCategory, searchTerm, aiFilteredProducts]);

  const categories = ['All', ...new Set(productItems.map(p => p.category || 'General'))];

  const handleProductClick = (productId) => {
    const recentlyViewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
    const updatedViewed = [productId, ...recentlyViewed.filter(id => id !== productId)].slice(0, 10);
    localStorage.setItem('recentlyViewed', JSON.stringify(updatedViewed));
    navigate(`/product/${productId}`);
  };

  return (
    <div className="product-list-container">
      {/* Header */}
      <div className="product-list-header">
        <h1>🛍️ Explore AR Products</h1>
        <p>Experience products in 3D and place them in your space with Augmented Reality</p>
        <div className="header-badges">
          <span className="tech-badge"><i className="fas fa-brain"></i> AI Powered</span>
          <span className="tech-badge blockchain"><i className="fas fa-shield-check"></i> Blockchain Verified</span>
          <span className="tech-badge ar"><i className="fas fa-cube"></i> AR Ready</span>
        </div>
      </div>

      {/* AI Features Quick Access Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '50px', maxWidth: '1200px', margin: '0 auto 50px auto' }}>
        <Link to="/ai-search" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '35px 30px', borderRadius: '24px', textDecoration: 'none', color: 'white', boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', overflow: 'hidden' }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(102, 126, 234, 0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(102, 126, 234, 0.3)'; }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'float 3s ease-in-out infinite' }}>🔍</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.5px' }}>AI Smart Search</h3>
          <p style={{ margin: 0, fontSize: '15px', opacity: 0.95, lineHeight: '1.5' }}>Natural language product search with intelligent filters</p>
          <div style={{ position: 'absolute', bottom: '15px', right: '15px', fontSize: '24px', opacity: 0.3 }}>→</div>
        </Link>
        <Link to="/visual-search" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', padding: '35px 30px', borderRadius: '24px', textDecoration: 'none', color: 'white', boxShadow: '0 10px 30px rgba(240, 147, 251, 0.3)', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', overflow: 'hidden' }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(240, 147, 251, 0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(240, 147, 251, 0.3)'; }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'float 3s ease-in-out infinite 0.5s' }}>📸</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.5px' }}>Visual Search</h3>
          <p style={{ margin: 0, fontSize: '15px', opacity: 0.95, lineHeight: '1.5' }}>Upload room photos for AI-powered recommendations</p>
          <div style={{ position: 'absolute', bottom: '15px', right: '15px', fontSize: '24px', opacity: 0.3 }}>→</div>
        </Link>
        <Link to="/compare" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', padding: '35px 30px', borderRadius: '24px', textDecoration: 'none', color: 'white', boxShadow: '0 10px 30px rgba(79, 172, 254, 0.3)', transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', position: 'relative', overflow: 'hidden' }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-8px)'; e.currentTarget.style.boxShadow = '0 15px 40px rgba(79, 172, 254, 0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(79, 172, 254, 0.3)'; }}>
          <div style={{ fontSize: '48px', marginBottom: '16px', animation: 'float 3s ease-in-out infinite 1s' }}>⚖️</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '22px', fontWeight: '700', letterSpacing: '-0.5px' }}>AI Compare</h3>
          <p style={{ margin: 0, fontSize: '15px', opacity: 0.95, lineHeight: '1.5' }}>Compare products with detailed AI analysis</p>
          <div style={{ position: 'absolute', bottom: '15px', right: '15px', fontSize: '24px', opacity: 0.3 }}>→</div>
        </Link>
      </div>

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div className="recommendations-showcase">
          <div className="recommendations-header">
            <h2><i className="fas fa-stars"></i> AI Picks For You</h2>
            <span className="ai-badge-small">Personalized</span>
          </div>
          <div className="recommendations-grid">
            {recommendations.map((product) => (
              <div key={product.id} className="recommendation-card" onClick={() => handleProductClick(product.id)} style={{ cursor: 'pointer' }}>
                <div className="rec-image" style={{ position: 'relative' }}>
                  <model-viewer src={product.modelSrc} ios-src={product.iOSSrc} alt={product.name} auto-rotate camera-controls style={{ width: '100%', height: '180px', borderRadius: '12px 12px 0 0', background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)' }} />
                  <div className="match-score">{product.aiScore}% Match</div>
                </div>
                <div className="rec-info">
                  <h4>{product.name}</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                    <span style={{ color: '#fbbf24', fontSize: '14px' }}>{'⭐'.repeat(Math.floor(parseFloat(product.rating)))}</span>
                    <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>{product.rating}</span>
                  </div>
                  <p className="rec-price">${product.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="product-controls">
        <div className="search-wrapper">
          <i className="fas fa-search search-icon-outside"></i>
          <div className="search-box">
            <input type="text" className="search-input" placeholder="Search products..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            {searchTerm && (<button className="clear-search" onClick={() => setSearchTerm('')} aria-label="Clear search"><i className="fas fa-times"></i></button>)}
          </div>
        </div>
        <div className="category-filters">
          {categories.map((cat) => (<button key={cat} className={`category-btn ${selectedCategory === cat ? 'active' : ''}`} onClick={() => setSelectedCategory(cat)}><i className={`fas fa-${cat === 'All' ? 'th' : 'tag'}`}></i>{cat}</button>))}
        </div>
      </div>

      <div className="products-count">
        <p>
          {aiFilteredProducts && (<span style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', marginRight: '10px', fontWeight: '600' }}>🤖 AI Search Results</span>)}
          Showing <strong>{filteredProducts.length}</strong> of <strong>{productItems.length}</strong> products
        </p>
        {aiFilteredProducts && (
          <button onClick={() => { setAiFilteredProducts(null); setFilteredProducts(productItems); setSearchTerm(''); setSelectedCategory('All'); }}
            style={{ background: 'transparent', border: '2px solid #667eea', color: '#667eea', padding: '8px 16px', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', marginLeft: '10px', marginTop: '10px', transition: 'all 0.3s ease' }}
            onMouseEnter={(e) => { e.target.style.background = '#667eea'; e.target.style.color = 'white'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; e.target.style.color = '#667eea'; }}>
            <i className="fas fa-times"></i> Clear AI Filter
          </button>
        )}
      </div>

      {/* Product Grid */}
      <section className="list-view">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((item, idx) => (
            <LazyLoad key={idx} height={200} offset={100}>
              <div className="product-card-wrapper" onMouseEnter={() => setHoveredProductId(item.id)} onMouseLeave={() => setHoveredProductId(null)}>
                <div className="card-authenticity"><AuthenticityBadge productId={item.id} compact={true} /></div>
                <ModelViewer item={item} addToWishlist={addToWishlist} wishlist={wishlist} removeFromWishlist={removeFromWishlist} />
                {hoveredProductId === item.id && (<button className="view-details-btn" onClick={() => handleProductClick(item.id)}><i className="fas fa-eye"></i> View Details</button>)}
                {recommendations.some(rec => rec.id === item.id) && (<div className="ai-recommended-badge"><i className="fas fa-sparkles"></i> AI Pick</div>)}
              </div>
            </LazyLoad>
          ))
        ) : (
          <div className="no-products">
            <div className="no-products-icon"><i className="fas fa-search"></i></div>
            <h3>No products found</h3>
            <p>Try adjusting your search or filters</p>
            <button className="reset-filters-btn" onClick={() => { setSearchTerm(''); setSelectedCategory('All'); }}><i className="fas fa-redo"></i> Reset Filters</button>
            {aiFilteredProducts && (<button className="reset-filters-btn" onClick={() => { setAiFilteredProducts(null); setFilteredProducts(productItems); setSearchTerm(''); setSelectedCategory('All'); }} style={{ marginTop: '10px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}><i className="fas fa-robot"></i> Clear AI Search</button>)}
          </div>
        )}
      </section>

      {filteredProducts.length > 0 && (
        <div className="ai-tip">
          <div className="ai-tip-icon"><i className="fas fa-robot"></i></div>
          <div className="ai-tip-content">
            <h3>Need Help Choosing?</h3>
            <p>Try our AI-powered tools above: Smart Search for natural language queries, Visual Search for room-based recommendations, or Compare to analyze products side-by-side!</p>
          </div>
        </div>
      )}

      <div className="product-feature-highlights">
        <div className="product-feature-card"><i className="fas fa-brain"></i><h4>AI Recommendations</h4><p>Smart picks based on your preferences</p></div>
        <div className="product-feature-card"><i className="fas fa-camera"></i><h4>Visual Search</h4><p>Upload photos for personalized suggestions</p></div>
        <div className="product-feature-card"><i className="fas fa-cube"></i><h4>AR Experience</h4><p>See products in your real space</p></div>
        <div className="product-feature-card"><i className="fas fa-shield-alt"></i><h4>Blockchain Verified</h4><p>Every product authenticity guaranteed</p></div>
      </div>
    </div>
  );
};

export default ProductList;
